import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { LogIn, User, Lock, AlertCircle, Loader2 } from 'lucide-react'
import { AuthService } from '../services/authService'
import { loginSuccess, loginFailure, setLoading } from '../store/slices/authSlice'
import type { RootState } from '../store'
import { useBranding } from '../hooks/useBranding'
import { DEFAULT_APP_HOME } from '../config/routes'
import { Capacitor } from '@capacitor/core'
import { Browser } from '@capacitor/browser'
import { withTimezone } from '../utils/signupTimezone'

// Google Icon SVG component
const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
)

export const Login: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const dispatch = useDispatch()
  const { isAuthenticated, isLoading } = useSelector((state: RootState) => state.auth)
  const branding = useBranding()
  
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)

  const authService = new AuthService()

  // Google-only sign-in (production parity). When enabled, the username/password
  // form, "forgot password", and "sign up" affordances are hidden and Google is
  // the sole sign-in path. Defaults ON; set VITE_AUTH_GOOGLE_ONLY=false to expose
  // the password form (local/dev). The backend independently rejects password
  // auth when AUTH_GOOGLE_ONLY is set, so hiding the UI is not the only guard.
  const googleOnly =
    (((import.meta as any).env?.VITE_AUTH_GOOGLE_ONLY ?? 'true') as string) !== 'false'

  // Resolve where to send the user after a successful login. Precedence:
  //   1. `?redirect=<path>` query param (set by <ProtectedRoute/> when an
  //      unauthenticated user hit a protected route)
  //   2. React Router `location.state.from.pathname` (legacy)
  //   3. DEFAULT_APP_HOME (/today)
  // Only internal paths are honored; anything that could start a new origin
  // (//host, http:, https:, protocol-relative) is ignored to prevent the
  // classic open-redirect pitfall.
  const resolvePostLoginTarget = (): string => {
    const raw = searchParams.get('redirect')
    if (raw) {
      try {
        const decoded = decodeURIComponent(raw)
        const isInternal =
          decoded.startsWith('/') &&
          !decoded.startsWith('//') &&
          !/^https?:/i.test(decoded)
        if (isInternal) return decoded
      } catch {
        /* malformed redirect= param; fall through */
      }
    }
    const fromPath = (location.state as any)?.from?.pathname
    if (typeof fromPath === 'string' && fromPath.startsWith('/')) {
      const fromSearch = (location.state as any)?.from?.search ?? ''
      const fromHash = (location.state as any)?.from?.hash ?? ''
      return `${fromPath}${fromSearch}${fromHash}`
    }
    return DEFAULT_APP_HOME
  }

  const friendlyAuthMessage = (codeOrMessage: string) => {
    const key = (codeOrMessage || '').trim().toLowerCase()
    if (key === 'account_pending_admin_approval') {
      return 'Account creation pending admin approval. Please await email for more instructions.'
    }
    return null
  }

  // Show message from redirects (e.g., pending approval after registration)
  useEffect(() => {
    const msg = (location.state as any)?.message
    if (msg) setError(friendlyAuthMessage(msg) || msg)
  }, [location.state])

  // Check for OAuth error in URL params
  useEffect(() => {
    const oauthError = searchParams.get('error')
    if (oauthError) {
      const friendly = friendlyAuthMessage(oauthError)
      if (friendly) {
        setError(friendly)
      } else {
        setError(`Google sign-in failed: ${oauthError.replace(/_/g, ' ')}`)
      }
    }
  }, [searchParams])

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(resolvePostLoginTarget(), { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, navigate, location, searchParams])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsSubmitting(true)
    dispatch(setLoading(true))

    try {
      const response = await authService.login({ username, password, rememberMe })
      
      // Check if MFA is required
      if (response.success && response.mfa_required) {
        // Redirect to MFA challenge page with user ID and remember preference
        navigate('/mfa-challenge', { 
          state: { userId: response.user_id, rememberMe },
          replace: true 
        })
        return
      }
      
      if (response.success && response.user) {
        dispatch(loginSuccess({ 
          user: {
            id: response.user.id.toString(),
            username: response.user.username,
            email: response.user.email
          },
          token: 'session' // Using session-based auth, no token needed
        }))
        
        // Redirect to the page they were trying to access, or Today's Focus
        navigate(resolvePostLoginTarget(), { replace: true })
      } else {
        setError(response.error || 'Login failed')
        dispatch(loginFailure())
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Login failed. Please try again.'
      setError(errorMessage)
      dispatch(loginFailure())
    } finally {
      setIsSubmitting(false)
      dispatch(setLoading(false))
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-default py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <img src={branding.icon} alt={branding.name} className="mx-auto h-16 w-16 mb-4" />
          <h2 className="text-3xl font-extrabold text-primary">
            Welcome back
          </h2>
          {/* 2026-08-29: the persona name was hardcoded to the diary theme's. */}
          <p className="mt-2 text-sm text-secondary">
            Ready to keep going? {branding.assistant} is here with you.
          </p>
        </div>

        <Card className="shadow-xl">
          <CardHeader>
            <CardTitle>Login</CardTitle>
            <CardDescription>
              {googleOnly
                ? 'Sign in with your Google account to continue'
                : 'Enter your credentials to access your account'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* 2026-08-29: the error appeared silently — no live region, and
                  nothing tied it to the fields it is about. */}
              <div aria-live="assertive" aria-atomic="true">
                {error && (
                  <div
                    id="login-error"
                    role="alert"
                    className="p-3 bg-tint-bad-veil border border-tint-bad-edge rounded-lg flex items-center space-x-2"
                  >
                    <AlertCircle className="h-5 w-5 text-bad flex-shrink-0" aria-hidden="true" />
                    <p className="text-sm text-bad">{error}</p>
                  </div>
                )}
              </div>

              {!googleOnly && (
              <>
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-secondary mb-1">
                  Username
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-muted" />
                  </div>
                  <input
                    id="username"
                    name="username"
                    type="text"
                    required
                    autoComplete="username"
                    // iOS auto-capitalises the first letter of a text input,
                    // so "venu" arrives as "Venu" and a case-sensitive login
                    // fails on the user's FIRST attempt with no clue why
                    // (seen 2026-08-30 on the iPhone 17 simulator). Usernames
                    // are never sentences: no capitalisation, no correction,
                    // no spellcheck.
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    aria-invalid={error ? true : undefined}
                    aria-describedby={error ? 'login-error' : undefined}
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-soft rounded-md focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent bg-surface text-primary"
                    placeholder="Enter your username"
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label htmlFor="password" className="block text-sm font-medium text-secondary">
                    Password
                  </label>
                  <a href="/forgot-password" className="text-sm font-medium text-brand hover:text-brand-hover">
                    Forgot password?
                  </a>
                </div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-muted" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    required
                    autoComplete="current-password"
                    aria-invalid={error ? true : undefined}
                    aria-describedby={error ? 'login-error' : undefined}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-soft rounded-md focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent bg-surface text-primary"
                    placeholder="Enter your password"
                    disabled={isSubmitting}
                  />
                </div>
              </div>
              </>
              )}

              <div className="rounded-lg border border-soft bg-surface-alt/50 p-3 space-y-1">
                <div className="flex items-start gap-2">
                  <input
                    id="keep-signed-in"
                    name="keep-signed-in"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="h-4 w-4 mt-0.5 text-brand focus:ring-brand border-soft rounded shrink-0"
                    disabled={isSubmitting || isGoogleLoading}
                  />
                  <div>
                    <label htmlFor="keep-signed-in" className="block text-sm font-medium text-primary cursor-pointer">
                      Keep me signed in on this device
                    </label>
                    <p className="text-xs text-secondary mt-0.5">
                      Applies when you sign in with password or Google.
                    </p>
                  </div>
                </div>
              </div>

              {!googleOnly && (
              <Button
                type="submit"
                className="w-full bg-brand hover:bg-brand-hover text-on-brand"
                disabled={isSubmitting || isLoading}
              >
                {isSubmitting || isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  <>
                    <LogIn className="h-4 w-4 mr-2" />
                    Sign in
                  </>
                )}
              </Button>
              )}

              {/* Divider */}
              {!googleOnly && (
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-soft"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-surface text-secondary">or continue with</span>
                </div>
              </div>
              )}

              {/* Google Sign In Button */}
              <Button
                type="button"
                variant={googleOnly ? 'default' : 'outline'}
                className={googleOnly
                  ? 'w-full bg-surface border border-soft hover:bg-surface-alt text-primary'
                  : 'w-full border-soft hover:bg-surface-alt'}
                disabled={isGoogleLoading}
                onClick={async () => {
                  setIsGoogleLoading(true)
                  const r = rememberMe ? '1' : '0'
                  if (Capacitor.isNativePlatform()) {
                    await Browser.open({
                      url: withTimezone(`https://horton.io/auth/google?mobile=1&remember=${r}`),
                    })
                  } else {
                    window.location.href = withTimezone(`/auth/google?remember=${r}`)
                  }
                }}
              >
                {isGoogleLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    <GoogleIcon />
                    <span className="ml-2">Sign in with Google</span>
                  </>
                )}
              </Button>

              <div className="text-center space-y-2 mt-4">
                <div className="text-sm">
                  <span className="text-secondary">Don't have an account? </span>
                  <a href="/register" className="font-medium text-brand hover:text-brand-hover">
                    Sign up
                  </a>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
